import { useParams } from 'next/navigation';

import classNames from 'classnames';

import { ExternalLink } from '@uikit';

import { useLaunchpadSaleQuery } from '@query';

import { LAUNCHPAD_SALE_POOL_TYPE } from '@api';

import { NewTab } from '@uikit/icons/NewTab';

import BaseInfoTooltip from '@shared/common/components/BaseInfoTooltip';
import { LAUNCHPAD_TOKEN_INFO } from '@shared/constants';
import { formatNumberAsCurrency, formatNumberLocalized } from '@shared/helpers/formatters';

import { getPoolInfo } from '@launchpad/components/pages/Project/ui/OptionsCard/PoolCard';
import { useMyBuyingPower, useTotalBuyingPower } from '@launchpad/hooks/useBuyingPower';
import useDepositTokenQuery from '@launchpad/hooks/useDepositTokenQuery';
import { useLaunchpadTotalDeposited } from '@launchpad/hooks/useLaunchpadTotalDeposited';
import useSelfDepositedAmountQuery from '@launchpad/hooks/useSelfDepositedAmountQuery';
import { isProRataType } from '@launchpad/utils/contract';

const calcOverfundRatio = (goal?: number, deposited?: number) => {
  if (!goal || !deposited) {
    return 0;
  }

  const result = deposited / goal;

  return formatNumberLocalized(result);
};

const Allocation = () => {
  const params = useParams<{ saleId: string }>();
  const { data: sale, isPending: isLoadingSale } = useLaunchpadSaleQuery(params.saleId);
  const maxAllocation = sale?.attributes.maxAllocation || 0;
  const { data: totalDepositedNumb, isPending: isLoadingTotalDeposited } =
    useLaunchpadTotalDeposited({
      depositContractAddress: sale?.attributes.depositContractAddress,
      type: sale?.attributes.type,
    });
  const { data: selfDepositInfo, isPending: isLoadingSelfDeposite } = useSelfDepositedAmountQuery({
    depositContractAddress: sale?.attributes.depositContractAddress,
    type: sale?.attributes.type,
  });
  const selfDepositedAmount = selfDepositInfo?.baseDeposited || 0;
  const { data: myBuyingPower, isPending: isLoadingMyBuyingPower } = useMyBuyingPower({
    depositContractAddress: sale?.attributes.depositContractAddress,
    type: sale?.attributes.type,
  });
  const { data: totalBuyingPower, isPending: isLoadingTotalBuyingPower } = useTotalBuyingPower({
    depositContractAddress: sale?.attributes.depositContractAddress,
    type: sale?.attributes.type,
  });
  const { data: depositTokenInfo, isPending: isLoadingDepositToken } = useDepositTokenQuery({
    depositContractAddress: sale?.attributes.depositContractAddress,
    type: sale?.attributes.type,
  });

  const availableAllocation = Math.max(maxAllocation - selfDepositedAmount, 0);

  const poolInfo = getPoolInfo(sale);

  const showSqrpBurned =
    sale?.attributes.type === LAUNCHPAD_SALE_POOL_TYPE.SQRP_GATED ||
    sale?.attributes.type === LAUNCHPAD_SALE_POOL_TYPE.PRO_RATA_SQRP_GATED;

  const isLoading = isLoadingSale || isLoadingSelfDeposite || isLoadingDepositToken;

  const overFoundRatio = calcOverfundRatio(
    sale?.attributes.goal,
    totalBuyingPower ?? totalDepositedNumb,
  );

  const isProRataSale = isProRataType(sale?.attributes.type);

  const showBuyingPowerInfo = isProRataSale && sale?.attributes.turboChargeAvailable;

  return (
    <div className="p-6 rounded-lg border border-primary-200">
      <h5 className="t-h3 text-[32px]">Pool Sale Details</h5>
      <div className="py-6 flex flex-col gap-4">
        <div className="flex flex-row justify-between">
          <span className="flex items-center">
            <span className="font-bold">Sale Type</span>

            <BaseInfoTooltip className="ml-1.5" content={poolInfo.tooltip} />
          </span>
          <div>
            {isLoading ? (
              <span className="skeleton-text w-28" />
            ) : (
              <>
                <span className="font-medium text-right">
                  {sale?.meta?.whiteListExists ? poolInfo?.whiteListName : poolInfo?.typeName}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-row justify-between">
          <span className="flex items-center">
            <span className="font-bold">Available Deposit</span>

            <BaseInfoTooltip
              className="ml-1.5"
              content="Displays the maximum deposit available for you in this IDO pool, allowing you to select any amount between the minimum and this maximum."
            />
          </span>
          <div>
            {isLoading ? (
              <span className="skeleton-text w-28" />
            ) : (
              <>
                <span className="text-[#0B801D] font-medium text-end">
                  {formatNumberLocalized(availableAllocation)}
                </span>{' '}
                <span className="text-[#0B801D] font-medium">
                  {depositTokenInfo?.symbol || LAUNCHPAD_TOKEN_INFO.symbol}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-row justify-between items-start">
          <span className="flex items-center">
            <span className="font-bold">My Deposits</span>
            <BaseInfoTooltip
              className="ml-1.5"
              content={`Displays the amount of ${
                depositTokenInfo?.symbol || LAUNCHPAD_TOKEN_INFO.symbol
              } and SQRp you have currently deposited in this IDO pool.`}
            />
          </span>
          <div className="text-end">
            {isLoading ? (
              <span className="skeleton-text w-28" />
            ) : (
              <>
                <span
                  className={classNames('font-medium text-end', {
                    'text-neutral-400': selfDepositedAmount === 0,
                  })}
                >
                  {formatNumberLocalized(selfDepositedAmount)}{' '}
                </span>
                <span
                  className={classNames('font-medium', {
                    'text-neutral-400': selfDepositedAmount === 0,
                  })}
                >
                  {depositTokenInfo?.symbol || LAUNCHPAD_TOKEN_INFO.symbol}
                </span>
              </>
            )}
            {showSqrpBurned && (
              <>
                <span />
                <div>
                  <span className="text-neutral-400 font-medium text-end">
                    {formatNumberLocalized(selfDepositedAmount)}{' '}
                  </span>
                  <span className="text-neutral-400 font-medium">SQRp</span>
                </div>
              </>
            )}
          </div>
        </div>

        {showBuyingPowerInfo && (
          <div className="flex flex-row justify-between items-start">
            <span className="flex items-center">
              <span className="font-bold">My Buying Power</span>
              <BaseInfoTooltip
                className="ml-1.5"
                content={`This number represents your buying power in this IDO pool; your final allocation will be determined relative to the buying power of all other participants.`}
              />
            </span>
            <div className="text-end">
              {isLoadingMyBuyingPower ? (
                <span className="skeleton-text w-28" />
              ) : (
                <>
                  <span
                    className={classNames('font-medium text-end', {
                      'text-neutral-400': myBuyingPower === 0,
                    })}
                  >
                    {formatNumberLocalized(myBuyingPower || 0)}{' '}
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        <hr className="w-full mt-4 border-primary-200" />

        <div className="flex flex-row justify-between">
          <span>
            {' '}
            <span className="font-bold text-neutral-500">Pool Raise Goal</span>
            <BaseInfoTooltip
              className="ml-1.5"
              content={
                sale?.attributes.depositTokenTicker === 'SQR'
                  ? `Pool size indicating the amount of funds to be raised in SQR tokens. Each SQR token is valued at ${formatNumberAsCurrency(
                      {
                        amount: sale?.attributes.depositTokenPrice || 0,
                      },
                    )}`
                  : 'Pool size indicating the amount of funds to be raised.'
              }
            />
          </span>
          <div>
            {isLoading ? (
              <span className="skeleton-text w-28" />
            ) : (
              <>
                <span className="text-neutral-600 font-medium text-end">
                  {formatNumberLocalized(sale?.attributes.goal || 0)}
                </span>{' '}
                <span className="text-neutral-600 font-medium">
                  {depositTokenInfo?.symbol || LAUNCHPAD_TOKEN_INFO.symbol}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-row justify-between">
          <span className="flex items-center">
            <span className="font-bold text-neutral-500">Total Deposits</span>
            <BaseInfoTooltip className="ml-1.5" content="Total amount deposited to the pool." />
          </span>
          <div>
            {isLoadingTotalDeposited ? (
              <span className="skeleton-text w-28" />
            ) : (
              <>
                <span className="font-medium text-end">
                  {formatNumberLocalized(totalDepositedNumb || 0)}{' '}
                </span>
                <span className="font-medium">
                  {depositTokenInfo?.symbol || LAUNCHPAD_TOKEN_INFO.symbol}
                </span>
              </>
            )}
          </div>
        </div>
        {showBuyingPowerInfo && (
          <div className="flex flex-row justify-between">
            <span className="flex items-center">
              <span className="font-bold text-neutral-500">Total Buying Power</span>
              <BaseInfoTooltip
                className="ml-1.5"
                content="This number represents the current total buying power of all participants in this IDO pool; based on this, your final allocation will be determined as your portion of the entire buying power."
              />
            </span>
            <div>
              {isLoadingTotalBuyingPower ? (
                <span className="skeleton-text w-28" />
              ) : (
                <span className="font-medium text-end">
                  {formatNumberLocalized(totalBuyingPower || 0)}{' '}
                </span>
              )}
            </div>
          </div>
        )}
        {isProRataSale && (
          <div className="flex flex-row justify-between">
            <span className="flex items-center">
              <span className="font-bold text-neutral-500">Subscribed</span>
              <BaseInfoTooltip
                className="ml-1.5"
                content="Shows the multiplier of the total buying power over the raise goal."
              />
            </span>
            <div>
              {isLoadingTotalDeposited ? (
                <span className="skeleton-text w-28" />
              ) : (
                <span className="font-medium text-end">{overFoundRatio}x</span>
              )}
            </div>
          </div>
        )}
        {sale?.attributes.status !== 'ANNOUNCED' && (
          <div className="flex flex-row justify-between">
            <span className="flex items-center">
              <span className="font-bold text-neutral-500">Deposit Contract</span>
              <BaseInfoTooltip
                className="ml-1.5"
                content="Access the deposit smart contract for this IDO pool."
              />
            </span>
            <div className="flex">
              {isLoading ? (
                <span className="skeleton-text w-28" />
              ) : (
                <>
                  <ExternalLink
                    className="text-right inline-flex"
                    href={`https://bscscan.com/address/${sale?.attributes.depositContractAddress}`}
                  >
                    <NewTab className="icon-sm text-primary-700" />
                  </ExternalLink>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Allocation;
